import React, { useRef, useEffect, useCallback } from 'react';
import { handleAreaErase, drawEraserPreview } from './utils/eraseUtils';
import MultiFactorPalmDetector from '../../utils/PalmDetection.js';
import { RedrawThrottler, ViewportCuller, PathCache } from './canvasOptimizations';
import { backgroundRenderers } from './utils/backgroundRenderers';
import { renderStroke } from './utils/strokeRenderer';
import { 
    calculateStrokesBounds,
    strokeIntersectsRect,
    getStrokesInSelection,
    transformStrokePoints,
    getResizeHandleAtPoint,
    getRotationHandleAtPoint,
    isPointInSelectionBounds,
    getCursorForHandle,
    drawSelectionVisuals
} from './utils/selectionUtils';
import {
    detectInputMethod,
    calculateTouchArea,
    getAreaThreshold,
    shouldRejectTouchOptimized,
    updateActiveTouches,
    getPrimaryTouch
} from './utils/palmRejectionUtils';
import { useRuler } from './hooks/useRuler';
import { useSelection } from './hooks/useSelection';
import { usePalmRejection } from './hooks/usePalmRejection';
import { useDrawing } from './hooks/useDrawing';
import { useTextTool } from './hooks/useTextTool';
import { useCanvasScroll } from './hooks/useCanvasScroll';
import './DrawingCanvas.css';

/**
 * DrawingCanvas - Optimized Palm Rejection System
 * 
 * Fully refactored palm rejection implementation with:
 * ✅ Step 1: Removed complex temporal analysis
 * ✅ Step 2: Simplified palm rejection logic  
 * ✅ Step 3: Cleaned up state management
 * ✅ Step 4: Improved input method detection
 * ✅ Step 5: Optimized multi-touch handling
 * ✅ Step 6: Cleaned up imports and dependencies
 * 
 * Benefits: Predictable, performant, debuggable palm rejection
 */

function DrawingCanvas({ 
    width = 800, 
    height = 600, 
    tool = 'pen',
    toolSettings = { color: '#000000', thickness: 2, opacity: 1.0, smoothing: 'medium' },
    onStrokeAdded,
    onStrokesUpdated, // New callback for when strokes are modified (moved/resized)
    onStrokeContextMenu, // New callback for context menu on strokes
    strokes = [],
    background = 'white',
    palmRejectionSettings = { enabled: false, sensitivity: 'medium', inputMode: 'auto', edgeRejection: true, multiTouchFiltering: true, showDebug: false },
    rulerVisible = false,
    setRulerVisible,
    pageMode = 'scaling', // New prop for page mode
    isExpanding = false, // New prop to show expansion animation
    contentBounds = null, // New prop for content bounds in infinite mode
    customBackgroundRenderer = null // New prop for custom background rendering
}) {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const redrawThrottlerRef = useRef(null);
    const viewportCullerRef = useRef(null);
    const pathCacheRef = useRef(null);
    const scrollTimeoutRef = useRef(null);
    const palmDetector = useRef(null);
    
    // Custom hooks for state management
    const [rulerState, rulerActions] = useRuler(rulerVisible);
    const [selectionState, selectionActions] = useSelection();
    const [palmState, palmActions] = usePalmRejection();
    const [drawingState, drawingActions, currentStrokeRef] = useDrawing();
    const [textState, textActions] = useTextTool();
    const [scrollState, scrollActions] = useCanvasScroll();
    
    // Destructure state for easier access (compatibility with existing code)
    const { start: rulerStart, end: rulerEnd, isDragging: isDraggingRuler, isRotating: isRotatingRuler, dragOffset, rotatingEndpoint } = rulerState || {};
    const { 
        selectedStrokes, selectionBox, isSelecting, isDragging: isDraggingSelection, 
        isResizing: isResizingSelection, isRotating: isRotatingSelection, startPoint: selectionStartPoint,
        dragStartPoint, resizeHandle, bounds: selectionBounds, cursor: selectionCursor,
        originalStrokePoints, originalStrokeData, transformedStrokes, rotationAngle, initialRotationAngle
    } = selectionState || {};
    const { 
        rejectedTouches, activeTouches, recentRejections, activeStrokesByTouch, 
        primaryDrawingTouch, currentInputMethod 
    } = palmState || {};
    const { 
        isDrawing, currentStroke, lastPoint, isAreaErasing, eraserStartPoint, 
        eraserEndPoint, isCanvasLargerThanContainer 
    } = drawingState || {};
    const { position: textInputPosition, isVisible: isTextInputVisible, value: textInputValue } = textState || {};
    const { 
        scrollOffset, lastTouchDistance, isScrolling, isPinchZooming, 
        showExpansionIndicator, expansionDirection 
    } = scrollState || {};
    
    // Sync ruler visibility with prop and notify parent when ruler is toggled internally
    useEffect(() => {
        if (rulerState.visible !== rulerVisible) {
            if (setRulerVisible) {
                setRulerVisible(rulerState.visible);
            }
        }
    }, [rulerState.visible, rulerVisible, setRulerVisible]);
    
    useEffect(() => {
        rulerActions.setVisible(rulerVisible);
    }, [rulerVisible]);
    
    // Initialize palm detector with user settings
    useEffect(() => {
        if (!palmDetector.current) {
            palmDetector.current = new MultiFactorPalmDetector({
                sensitivity: palmRejectionSettings.sensitivity || 'medium',
                palmAreaThreshold: 200,
                fingerAreaThreshold: 100,
                stylusAreaThreshold: 50,
                palmPressureMax: 0.3,
                palmVelocityMax: 5,
                deviceType: navigator.platform?.includes('iPad') ? 'iPad' : 
                           navigator.platform?.includes('Win') ? 'Surface' : 
                           navigator.platform?.includes('Android') ? 'Android' : 'unknown',
                touchSensitivity: 1.0
            });
        }
    }, [palmRejectionSettings.sensitivity]);
    
    // Galaxy/Stars: cache star positions for static background
    const galaxyStarCache = useRef({ width: 0, height: 0, stars: [] });

    // Draw background pattern - dispatcher to background renderers
    const drawBackground = (ctx) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        // Use custom background renderer if provided
        if (customBackgroundRenderer) {
            customBackgroundRenderer(ctx);
            return;
        }

        // Get the appropriate renderer for this background
        const renderer = backgroundRenderers[background];
        
        if (renderer) {
            // Galaxy background needs the cache
            if (background === 'galaxy') {
                renderer(ctx, width, height, galaxyStarCache.current);
            } else {
                renderer(ctx, width, height);
            }
        } else {
            // Fallback to white background if renderer not found
            backgroundRenderers['white'](ctx, width, height);
        }
    };

    // Draw a single stroke with smooth curves (shared with the PDF export)
    const drawStroke = (ctx, stroke) => renderStroke(ctx, stroke, pathCacheRef.current);

    // ================== RULER FUNCTIONS ==================

    // Draw the ruler on the canvas
    const drawRuler = (ctx) => {
	if (!rulerVisible) return;

	ctx.save();
	
	// Draw ruler line
	ctx.strokeStyle = '#2563eb'; // Blue color
	ctx.lineWidth = 3;
	ctx.lineCap = 'round';
	ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
	ctx.shadowBlur = 4;
	ctx.shadowOffsetX = 1;
	ctx.shadowOffsetY = 1;
	
	ctx.beginPath();
	ctx.moveTo(rulerStart.x, rulerStart.y);
	ctx.lineTo(rulerEnd.x, rulerEnd.y);
	ctx.stroke();
	
	// Draw endpoints
	const endpointRadius = 8;
	
	// Start endpoint
	ctx.fillStyle = '#1d4ed8';
	ctx.beginPath();
	ctx.arc(rulerStart.x, rulerStart.y, endpointRadius, 0, 2 * Math.PI);
	ctx.fill();
	
	// End endpoint
	ctx.beginPath();
	ctx.arc(rulerEnd.x, rulerEnd.y, endpointRadius, 0, 2 * Math.PI);
	ctx.fill();
	
	// Draw small grip indicators
	ctx.fillStyle = '#ffffff';
	ctx.beginPath();
	ctx.arc(rulerStart.x, rulerStart.y, 3, 0, 2 * Math.PI);
	ctx.fill();
	
	ctx.beginPath();
	ctx.arc(rulerEnd.x, rulerEnd.y, 3, 0, 2 * Math.PI);
	ctx.fill();

	// --- Angle display ---
	try {
		// Calculate angle (radians) and convert to degrees
		const dx = rulerEnd.x - rulerStart.x;
		const dy = rulerEnd.y - rulerStart.y;
		const angleRad = Math.atan2(dy, dx);
		let angleDeg = (angleRad * 180) / Math.PI;
		// Normalize to 0-360
		angleDeg = ((angleDeg % 360) + 360) % 360;
		// For readability show smallest equivalent (e.g., 350° -> -10° not desired), keep 0-180 with sign?
		const displayDeg = Math.round(angleDeg * 10) / 10; // one decimal
		const angleLabel = `${displayDeg}°`;

		// Midpoint
		const midX = (rulerStart.x + rulerEnd.x) / 2;
		const midY = (rulerStart.y + rulerEnd.y) / 2;

		// Perpendicular direction (unit)
		const perpX = -Math.sin(angleRad);
		const perpY = Math.cos(angleRad);
		const offset = 18; // pixels away from ruler line
		const labelX = midX + perpX * offset;
		const labelY = midY + perpY * offset;

		// Draw label background box
		ctx.save();
		ctx.font = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		const paddingX = 8;
		const paddingY = 4;
		const metrics = ctx.measureText(angleLabel);
		const rectW = metrics.width + paddingX * 2;
		const rectH = 16 + paddingY * 2;

		// Slightly transparent white background with subtle border
		ctx.fillStyle = 'rgba(255,255,255,0.9)';
		ctx.strokeStyle = 'rgba(0,0,0,0.08)';
		ctx.lineWidth = 1;
		ctx.beginPath();
		ctx.rect(labelX - rectW / 2, labelY - rectH / 2, rectW, rectH);
		ctx.fill();
		ctx.stroke();

		// Draw angle text
		ctx.fillStyle = '#111827';
		ctx.fillText(angleLabel, labelX, labelY);
		ctx.restore();
	} catch (e) {
		// If something goes wrong drawing the label, don't break the rest
		console.error('Error drawing ruler angle label', e);
	}

	ctx.restore();
};

    // Check if a point is near the ruler line (within snap distance)
    const isPointNearRuler = (point, snapDistance = 30) => {
        if (!rulerVisible) return false;
        
        const distance = distanceFromPointToLine(point, rulerStart, rulerEnd);
        return distance <= snapDistance;
    };

    // Calculate distance from point to line segment
    const distanceFromPointToLine = (point, lineStart, lineEnd) => {
        const A = point.x - lineStart.x;
        const B = point.y - lineStart.y;
        const C = lineEnd.x - lineStart.x;
        const D = lineEnd.y - lineStart.y;

        const dot = A * C + B * D;
        const lenSq = C * C + D * D;
        
        if (lenSq === 0) return Math.sqrt(A * A + B * B); // Start and end are the same point
        
        let t = Math.max(0, Math.min(1, dot / lenSq));
        
        const projection = {
            x: lineStart.x + t * C,
            y: lineStart.y + t * D
        };
        
        const dx = point.x - projection.x;
        const dy = point.y - projection.y;
        
        return Math.sqrt(dx * dx + dy * dy);
    };

    // Project point onto ruler line
    const projectPointOntoRuler = (point) => {
        if (!rulerVisible) return point;
        
        const A = point.x - rulerStart.x;
        const B = point.y - rulerStart.y;
        const C = rulerEnd.x - rulerStart.x;
        const D = rulerEnd.y - rulerStart.y;

        const dot = A * C + B * D;
        const lenSq = C * C + D * D;
        
        if (lenSq === 0) return rulerStart; // Start and end are the same point
        
        let t = Math.max(0, Math.min(1, dot / lenSq));
        
        return {
            x: rulerStart.x + t * C,
            y: rulerStart.y + t * D
        };
    };

    // Check if point is near ruler endpoint
    const isPointNearRulerEndpoint = (point, endpoint, threshold = 20) => {
        const dx = point.x - endpoint.x;
        const dy = point.y - endpoint.y;
        return Math.sqrt(dx * dx + dy * dy) <= threshold;
    };

    // Check if point is on ruler body (for moving entire ruler)
    const isPointOnRulerBody = (point, threshold = 15) => {
        if (!rulerVisible) return false;
        
        // Check if close to line but not near endpoints
        const nearLine = isPointNearRuler(point, threshold);
        const nearStart = isPointNearRulerEndpoint(point, rulerStart, 20);
        const nearEnd = isPointNearRulerEndpoint(point, rulerEnd, 20);
        
        return nearLine && !nearStart && !nearEnd;
    };

    // ================== END RULER FUNCTIONS ==================

    // Handle context menu for saving strokes
    const handleContextMenu = (e) => {
        e.preventDefault();
        
        // Allow context menu on strokes when we have the handler and we're using select tool
        if (tool === 'select' && onStrokeContextMenu) {
            // Get the stroke under the mouse position
            const point = getCanvasCoordinates(e);
            const strokeUnderMouse = findStrokeAtPoint(point);
            
            if (strokeUnderMouse) {
                // If the stroke is not selected, select it first
                if (!selectedStrokes.has(strokeUnderMouse.id)) {
                    selectionActions.selectStrokes([strokeUnderMouse.id]);
                }
                
                // Call the context menu handler with the stroke
                onStrokeContextMenu(e, strokeUnderMouse);
                return;
            }
        }
        
        // Default: prevent context menu (but could allow native context menu for other tools)
    };

    // Find stroke at a given point
    const findStrokeAtPoint = (point) => {
        for (let i = strokes.length - 1; i >= 0; i--) {
            const stroke = strokes[i];
            if (isPointOnStroke(point, stroke)) {
                return stroke;
            }
        }
        return null;
    };

    // Check if a point is on a stroke (simplified hit testing)
    const isPointOnStroke = (point, stroke) => {
        if (!stroke.points || stroke.points.length < 2) return false;
        
        const threshold = (stroke.style?.strokeWidth || 2) + 5; // Add some tolerance
        
        for (let i = 0; i < stroke.points.length - 1; i++) {
            const p1 = stroke.points[i];
            const p2 = stroke.points[i + 1];
            
            const distance = distanceToLineSegment(point, p1, p2);
            if (distance <= threshold) {
                return true;
            }
        }
        return false;
    };

    // Calculate distance from point to line segment
    const distanceToLineSegment = (point, lineStart, lineEnd) => {
        const A = point.x - lineStart.x;
        const B = point.y - lineStart.y;
        const C = lineEnd.x - lineStart.x;
        const D = lineEnd.y - lineStart.y;

        const dot = A * C + B * D;
        const lenSq = C * C + D * D;
        
        if (lenSq === 0) {
            // Line start and end are the same point
            return Math.sqrt(A * A + B * B);
        }

        let t = Math.max(0, Math.min(1, dot / lenSq));

        const projection = {
            x: lineStart.x + t * C,
            y: lineStart.y + t * D
        };

        const dx = point.x - projection.x;
        const dy = point.y - projection.y;
        
        return Math.sqrt(dx * dx + dy * dy);
    };

    // Selection utility functions are now imported from utils/selectionUtils.js

    // Update selection bounds when selected strokes change
    const updateSelectionBounds = () => {
        if (selectedStrokes.size > 0) {
            const bounds = calculateStrokesBounds(selectedStrokes, strokes);
            selectionActions.setBounds(bounds);
        } else {
            selectionActions.setBounds(null);
        }
    };

    // Update cursor based on mouse position for selection tool
    const updateSelectionCursor = (point) => {
        if (tool !== 'select') {
            selectionActions.setCursor('default');
            return;
        }

        if (selectionBounds) {
            // Check rotation handle first
            if (getRotationHandleAtPoint(point, selectionBounds)) {
                selectionActions.setCursor('grab');
                return;
            }
            
            const handle = getResizeHandleAtPoint(point, selectionBounds);
            if (handle) {
                selectionActions.setCursor(getCursorForHandle(handle));
                return;
            }
            
            if (isPointInSelectionBounds(point, selectionBounds)) {
                selectionActions.setCursor('move');
                return;
            }
        }
        
        selectionActions.setCursor('crosshair'); // Default for selection tool
    };

    // Update cursor when rotating
    useEffect(() => {
        if (isRotatingSelection) {
            selectionActions.setCursor('grabbing');
        }
    }, [isRotatingSelection]);

    // ================== SCROLL FUNCTIONS FOR INFINITE PAGES ==================

    // Handle mouse wheel scrolling
    const handleWheel = useCallback((event) => {
        if (pageMode !== 'infinite') return;
        
        event.preventDefault();
        
        const deltaX = event.deltaX;
        const deltaY = event.deltaY;
        
        // Apply scroll limits based on content bounds
        const maxScrollX = Math.max(0, (contentBounds?.width || width) - width);
        const maxScrollY = Math.max(0, (contentBounds?.height || height) - height);
        
        scrollActions.setScrollOffset(prev => ({
            x: Math.max(0, Math.min(maxScrollX, prev.x + deltaX)),
            y: Math.max(0, Math.min(maxScrollY, prev.y + deltaY))
        }));
        
        // Show scrolling indicator
        scrollActions.startScrolling();
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = setTimeout(() => {
            scrollActions.stopScrolling();
        }, 150);
    }, [pageMode, contentBounds, width, height]);

    // Calculate distance between two touch points
    const getTouchDistance = (touches) => {
        if (touches.length < 2) return 0;
        const touch1 = touches[0];
        const touch2 = touches[1];
        const dx = touch1.clientX - touch2.clientX;
        const dy = touch1.clientY - touch2.clientY;
        return Math.sqrt(dx * dx + dy * dy);
    };

    // Handle two-finger scrolling and pinch detection
    const handleTouchScroll = useCallback((event) => {
        if (pageMode !== 'infinite') return false;
        
        const touches = Array.from(event.touches);
        
        // Only handle two-finger gestures
        if (touches.length !== 2) {
            scrollActions.setLastTouchDistance(0);
            scrollActions.stopPinchZoom();
            return false;
        }
        
        event.preventDefault();
        
        const currentDistance = getTouchDistance(touches);
        const touch1 = touches[0];
        const touch2 = touches[1];
        
        // Calculate center point of the two touches
        const centerX = (touch1.clientX + touch2.clientX) / 2;
        const centerY = (touch1.clientY + touch2.clientY) / 2;
        
        if (event.type === 'touchstart') {
            scrollActions.setLastTouchDistance(currentDistance);
            // Store the initial center position for scroll calculation
            window.scrollStartCenter = { x: centerX, y: centerY };
            return true;
        }
        
        if (event.type === 'touchmove') {
            // Detect pinch vs scroll
            if (lastTouchDistance > 0) {
                const distanceChange = Math.abs(currentDistance - lastTouchDistance);
                const distanceThreshold = 15;
                
                if (distanceChange > distanceThreshold) {
                    scrollActions.startPinchZoom();
                    // Don't handle as scroll if pinching
                    scrollActions.setLastTouchDistance(currentDistance);
                    return true;
                }
            }
            
            // Handle as scroll gesture
            if (window.scrollStartCenter && !isPinchZooming) {
                const deltaX = (centerX - window.scrollStartCenter.x) * -1; // Invert for natural scrolling
                const deltaY = (centerY - window.scrollStartCenter.y) * -1;
                
                // Apply scroll limits
                const maxScrollX = Math.max(0, (contentBounds?.width || width) - width);
                const maxScrollY = Math.max(0, (contentBounds?.height || height) - height);
                
                scrollActions.setScrollOffset(prev => ({
                    x: Math.max(0, Math.min(maxScrollX, prev.x + deltaX * 0.8)),
                    y: Math.max(0, Math.min(maxScrollY, prev.y + deltaY * 0.8))
                }));
                
                // Update scroll start position for continuous scrolling
                window.scrollStartCenter = { x: centerX, y: centerY };
                
                // Show scrolling indicator
                scrollActions.startScrolling();
                if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                }
                scrollTimeoutRef.current = setTimeout(() => {
                    scrollActions.stopScrolling();
                }, 150);
            }
            
            scrollActions.setLastTouchDistance(currentDistance);
            return true;
        }
        
        if (event.type === 'touchend') {
            scrollActions.setLastTouchDistance(0);
            scrollActions.stopPinchZoom();
            delete window.scrollStartCenter;
            return true;
        }
        
        return false;
    }, [pageMode, contentBounds, width, height, lastTouchDistance, isPinchZooming]);

    // Transform coordinates to account for scroll offset
    const transformCoordinates = (point) => {
        if (pageMode !== 'infinite') return point;
        
        return {
            x: point.x + scrollOffset.x,
            y: point.y + scrollOffset.y
        };
    };

    // Transform coordinates back from canvas to viewport (for drawing)
    const untransformCoordinates = (point) => {
        if (pageMode !== 'infinite') return point;
        
        return {
            x: point.x - scrollOffset.x,
            y: point.y - scrollOffset.y
        };
    };

    // ================== END SCROLL FUNCTIONS ==================

    // Draw expansion indicators for infinite pages
    const drawExpansionIndicators = (ctx) => {
        if (!showExpansionIndicator || !expansionDirection) return;
        
        ctx.save();
        
        // Set up expansion indicator style
        ctx.strokeStyle = '#4A90E2';
        ctx.fillStyle = 'rgba(74, 144, 226, 0.1)';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        
        const indicatorSize = 30;
        const margin = 10;
        
        // Right expansion indicator
        if (expansionDirection === 'right' || expansionDirection === 'both') {
            const x = width - margin - indicatorSize;
            const y = height / 2;
            
            // Draw expansion arrow
            ctx.beginPath();
            ctx.rect(x, y - indicatorSize/2, indicatorSize, indicatorSize);
            ctx.fill();
            ctx.stroke();
            
            // Draw arrow
            ctx.beginPath();
            ctx.moveTo(x + 8, y);
            ctx.lineTo(x + indicatorSize - 8, y);
            ctx.moveTo(x + indicatorSize - 12, y - 4);
            ctx.lineTo(x + indicatorSize - 8, y);
            ctx.lineTo(x + indicatorSize - 12, y + 4);
            ctx.strokeStyle = '#4A90E2';
            ctx.setLineDash([]);
            ctx.stroke();
        }
        
        // Down expansion indicator
        if (expansionDirection === 'down' || expansionDirection === 'both') {
            const x = width / 2;
            const y = height - margin - indicatorSize;
            
            // Draw expansion arrow
            ctx.beginPath();
            ctx.rect(x - indicatorSize/2, y, indicatorSize, indicatorSize);
            ctx.fill();
            ctx.stroke();
            
            // Draw arrow
            ctx.beginPath();
            ctx.moveTo(x, y + 8);
            ctx.lineTo(x, y + indicatorSize - 8);
            ctx.moveTo(x - 4, y + indicatorSize - 12);
            ctx.lineTo(x, y + indicatorSize - 8);
            ctx.lineTo(x + 4, y + indicatorSize - 12);
            ctx.strokeStyle = '#4A90E2';
            ctx.setLineDash([]);
            ctx.stroke();
        }
        
        ctx.restore();
    };

    // Redraw all strokes on the canvas - access values directly to prevent closure issues
    const redrawCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }
        
        const ctx = canvas.getContext('2d');
        const devicePixelRatio = window.devicePixelRatio || 1;
        
        // Clear canvas (account for device pixel ratio)
        ctx.clearRect(0, 0, canvas.width / devicePixelRatio, canvas.height / devicePixelRatio);
        
        // Draw background first - call function directly
        drawBackground(ctx);
        
        // Draw all strokes - access current strokes
        const currentStrokes = strokes || [];
        
        // Create a map of transformed strokes for quick lookup during selection operations
        const transformedStrokeMap = new Map();
        if (transformedStrokes && Array.isArray(transformedStrokes) && transformedStrokes.length > 0) {
            transformedStrokes.forEach(stroke => {
                transformedStrokeMap.set(stroke.id, stroke);
            });
        }
        
        // Phase 2: Viewport Culling - Only render visible strokes (70-90% performance improvement)
        let visibleStrokes = currentStrokes;
        if (viewportCullerRef.current) {
            visibleStrokes = viewportCullerRef.current.filterVisibleStrokes(currentStrokes);
        }
        
        if (visibleStrokes && Array.isArray(visibleStrokes) && visibleStrokes.length > 0) {
            visibleStrokes.forEach((stroke) => {
                if (stroke && stroke.points && stroke.points.length > 0) {
                    // Use transformed version if available (during drag/resize operations)
                    const strokeToRender = transformedStrokeMap.get(stroke.id) || stroke;
                    
                    // Highlight selected strokes
                    if (selectedStrokes && selectedStrokes.has(stroke.id)) {
                        ctx.save();
                        ctx.shadowColor = '#0066cc';
                        ctx.shadowBlur = 3;
                        drawStroke(ctx, strokeToRender);
                        ctx.restore();
                    } else {
                        drawStroke(ctx, strokeToRender);
                    }
                }
            });
        }
        
        // Draw ruler if visible
        if (rulerVisible) {
            drawRuler(ctx);
        }
        
        // Draw selection visuals for select tool
        if (tool === 'select') {
            drawSelectionVisuals(ctx, isSelecting, selectionBox, selectedStrokes, selectionBounds);
        }
        
        // Draw area eraser preview when area erasing is active
        if ((tool === 'square-eraser' || tool === 'circle-eraser') && isAreaErasing && eraserStartPoint && eraserEndPoint) {
            drawEraserPreview(canvasRef, isAreaErasing, eraserStartPoint, eraserEndPoint, tool);
        }
    }, [strokes, background, selectedStrokes, rulerState.visible, rulerStart, rulerEnd, tool, isAreaErasing, eraserStartPoint, eraserEndPoint, isSelecting, selectionBox, selectionBounds, transformedStrokes]);

    // Palm rejection utility functions are now imported from utils/palmRejectionUtils.js
    
    // Wrapper functions to integrate imported palm rejection utilities with component state
    const updateInputMethod = (event) => {
        const settings = palmRejectionSettings || { inputMode: 'auto', enabled: false, sensitivity: 'medium' };
        const detected = detectInputMethod(event, settings);
        palmActions.setInputMethod(detected);
        return detected;
    };
    
    const updateActiveTouch = (event, eventType) => {
        const settings = palmRejectionSettings || { inputMode: 'auto', enabled: false, sensitivity: 'medium' };
        const newActiveTouches = updateActiveTouches(event, eventType, activeTouches, settings);
        palmActions.setActiveTouches(newActiveTouches);
        return newActiveTouches;
    };
    
    const shouldRejectTouch = (touch, inputMethod) => {
        const settings = palmRejectionSettings || { inputMode: 'auto', enabled: false, sensitivity: 'medium' };
        const trackRejection = (rejection) => {
            palmActions.setRecentRejections(prev => [...prev.slice(-10), rejection]);
        };
        
        return shouldRejectTouchOptimized(
            touch,
            inputMethod,
            settings,
            activeTouches,
            palmDetector.current,
            { isDrawing, primaryDrawingTouch },
            { width, height },
            trackRejection
        );
    };

    // ================== OPTIMIZED PALM REJECTION SYSTEM (Steps 1-6) ==================

    // Initialize canvas and redraw immediately when dimensions change
    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas && width && height) {
            const ctx = canvas.getContext('2d');
            const devicePixelRatio = window.devicePixelRatio || 1;
            
            // Set the actual canvas size in memory
            canvas.width = width * devicePixelRatio;
            canvas.height = height * devicePixelRatio;
            
            // Scale the canvas back down using CSS
            canvas.style.width = width + 'px';
            canvas.style.height = height + 'px';
            
            // Scale the drawing context so everything draws at the correct size
            ctx.scale(devicePixelRatio, devicePixelRatio);
            
            // Set drawing properties
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.imageSmoothingEnabled = true;
            
            // Immediately redraw to prevent blank canvas flicker
            redrawThrottlerRef.current?.requestRedraw() || redrawCanvas();
        }
    }, [width, height, redrawCanvas]);

    // Initialize redraw throttler
    useEffect(() => {
        redrawThrottlerRef.current = new RedrawThrottler(redrawCanvas);
        pathCacheRef.current = new PathCache(1000); // Cache up to 1000 stroke paths
        return () => {
            redrawThrottlerRef.current?.cancel();
        };
    }, [redrawCanvas]);
    
    // Initialize and update viewport culler
    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            if (!viewportCullerRef.current) {
                viewportCullerRef.current = new ViewportCuller(canvas.width, canvas.height, 100);
            } else {
                viewportCullerRef.current.updateViewport(canvas.width, canvas.height, 0, 0);
            }
        }
    }, [width, height]);
    
    // Redraw when content changes (using throttled version)
    useEffect(() => {
        // Skip full redraws during active drawing - incremental drawing in continueDrawing handles live rendering
        if (isDrawing) {
            return;
        }
        redrawThrottlerRef.current?.requestRedraw() || redrawCanvas();
    }, [strokes, background, redrawCanvas, isDrawing]);
    
    // Check if canvas is larger than container for proper centering (only for infinite mode)
    useEffect(() => {
        if (pageMode !== 'infinite') {
            // Always set to false for scaling mode to avoid any conflicts
            drawingActions.setCanvasLarger(false);
            return;
        }

        const checkCanvasSize = () => {
            const container = containerRef.current;
            if (container) {
                const containerRect = container.getBoundingClientRect();
                const isLarger = width > containerRect.width || height > containerRect.height;
                drawingActions.setCanvasLarger(isLarger);
            }
        };

        checkCanvasSize();
        
        // Also check on window resize (only for infinite mode)
        window.addEventListener('resize', checkCanvasSize);
        return () => window.removeEventListener('resize', checkCanvasSize);
    }, [width, height, pageMode]);
    
    // Update selection bounds when selected strokes change
    useEffect(() => {
        updateSelectionBounds();
    }, [selectedStrokes, strokes]);
    
    // Clear selection when switching away from select tool
    useEffect(() => {
        if (tool !== 'select') {
            selectionActions.selectStrokes([]);
            selectionActions.setSelectionBox(null);
            selectionActions.stopSelecting();
            selectionActions.stopDragging();
            selectionActions.stopResizing();
            selectionActions.stopRotating();
        }
    }, [tool]);
    
    // Redraw when eraser preview changes
    useEffect(() => {
        if (isAreaErasing) {
            redrawThrottlerRef.current?.requestRedraw() || redrawCanvas();
        }
    }, [eraserStartPoint, eraserEndPoint, isAreaErasing, redrawCanvas]);

    // Clean up old rejection markers - this stays for basic functionality
    useEffect(() => {
        const cleanup = setInterval(() => {
            palmActions.setRecentRejections(prev => 
                prev.filter(rejection => Date.now() - rejection.timestamp < 2000) // Remove after 2 seconds
            );
        }, 500);
        
        return () => clearInterval(cleanup);
    }, []);
    
    // Clean up touch isolation state when palm rejection settings change
    useEffect(() => {
        // Reset all touch tracking when palm rejection settings change
        palmActions.clearRejectedTouches();
        palmActions.setActiveStrokesByTouch(new Map());
        palmActions.setPrimaryDrawingTouch(null);
        palmActions.setActiveTouches(new Map());
    }, [palmRejectionSettings.enabled, palmRejectionSettings.sensitivity, palmRejectionSettings.multiTouchFiltering]);

    // Get coordinates relative to canvas for UI interactions (ruler, selection)
    const getCanvasCoordinates = (event) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0, pressure: 0.5 };
        
        const rect = canvas.getBoundingClientRect();
        
        // Calculate scale factors based on CSS size vs logical canvas size
        const scaleX = width / rect.width;
        const scaleY = height / rect.height;
        
        let clientX, clientY;
        
        if (event.touches && event.touches.length > 0) {
            // Touch event - use the first touch point
            const touch = event.touches[0];
            clientX = touch.clientX;
            clientY = touch.clientY;
        } else if (event.changedTouches && event.changedTouches.length > 0) {
            // Touch end event
            const touch = event.changedTouches[0];
            clientX = touch.clientX;
            clientY = touch.clientY;
        } else {
            // Mouse event
            clientX = event.clientX;
            clientY = event.clientY;
        }
        
        // Calculate precise coordinates - NO transformations for UI interactions
        const x = (clientX - rect.left) * scaleX;
        const y = (clientY - rect.top) * scaleY;
        
        return {
            x: Math.round(x),
            y: Math.round(y),
            pressure: event.pressure || (event.touches ? 0.5 : 0.5) // Default pressure
        };
    };

    // --- Drawing logic guards ---
    const isRulerInteracting = () => isDraggingRuler || isRotatingRuler;

    // Start drawing
    const startDrawing = (event) => {
        if (isRulerInteracting()) return;
        event.preventDefault();
        
        // Step 1: Get the specific touch for this event
        let touchToCheck = null;
        let touchId = 'pointer';
        
        if (event.touches && event.touches.length > 0) {
            touchToCheck = event.touches[0];
            touchId = touchToCheck.identifier;
        } else if (event.changedTouches && event.changedTouches.length > 0) {
            touchToCheck = event.changedTouches[0];
            touchId = touchToCheck.identifier;
        }
        
        // Step 2: IMMEDIATE palm rejection check BEFORE any stroke creation
        // This prevents palm touches from ever starting a stroke
        if (palmRejectionSettings.enabled && touchToCheck) {
            const inputMethod = updateInputMethod(event);
            const touchArea = calculateTouchArea(touchToCheck);
            const areaThreshold = getAreaThreshold(palmRejectionSettings.sensitivity);
            
            // Immediate rejection for large touch areas (palm/arm contact)
            if (touchArea > areaThreshold) {
                palmActions.addRejectedTouch(touchId);
                
                // Add visual feedback for rejection
                const canvas = canvasRef.current;
                if (canvas) {
                    const rect = canvas.getBoundingClientRect();
                    const x = touchToCheck.clientX - rect.left;
                    const y = touchToCheck.clientY - rect.top;
                    
                    palmActions.setRecentRejections(prev => [
                        ...prev,
                        {
                            id: Date.now() + Math.random(),
                            x,
                            y,
                            timestamp: Date.now(),
                            reason: 'immediate_palm_rejection'
                        }
                    ].slice(-5));
                }
                
                return; // Exit immediately, no stroke creation
            }
            
            // Immediate rejection for non-stylus when we already have active touches
            if ((activeTouches instanceof Map) && activeTouches.size > 0 && inputMethod !== 'stylus') {
                const hasActiveStylus = Array.from(activeTouches.values()).some(touch => touch.inputMethod === 'stylus');
                if (hasActiveStylus) {
                    palmActions.addRejectedTouch(touchId);
                    return;
                }
            }
        }
        
        // Step 3: Update input method and active touches AFTER initial rejection checks
        const inputMethod = updateInputMethod(event);
        const touches = updateActiveTouch(event, 'start');
        
        // Step 4: Additional palm rejection with full context
        if (touchToCheck && shouldRejectTouch(touchToCheck, inputMethod)) {
            // Reject this touch
            palmActions.addRejectedTouch(touchId);
            
            // Add visual feedback for rejection
            const canvas = canvasRef.current;
            if (canvas) {
                const rect = canvas.getBoundingClientRect();
                const x = touchToCheck.clientX - rect.left;
                const y = touchToCheck.clientY - rect.top;
                
                palmActions.setRecentRejections(prev => [
                    ...prev,
                    {
                        id: Date.now() + Math.random(),
                        x,
                        y,
                        timestamp: Date.now(),
                        reason: 'contextual_palm_rejection'
                    }
                ].slice(-5));
            }
        
            return; // Exit early, don't start drawing
        }
        
        // Step 5: Optimized multi-touch handling - consistent behavior
        if (palmRejectionSettings.enabled && touches.size > 1) {
            const primaryTouch = getPrimaryTouch(touches);
        }
        
        let point = getCanvasCoordinates(event);
        let strokePoint = point;
        if (rulerVisible && isPointNearRuler(point, 30)) {
            strokePoint = projectPointOntoRuler(point);
        }
        
        // Apply coordinate transformation for infinite page mode
        if (pageMode === 'infinite') {
            strokePoint = transformCoordinates(strokePoint);
        }
        
        if (tool === 'eraser') {
            // Handle point eraser tool - find and remove strokes at this position
            handleErase(getCanvasCoordinates(event));
            drawingActions.startDrawing(null);
            
            // Set this as the primary drawing touch for point eraser too
            if (!primaryDrawingTouch) {
                palmActions.setPrimaryDrawingTouch(touchId);
            }
            return;
        }
        
        if (tool === 'square-eraser' || tool === 'circle-eraser') {
            // Start area erasing
            const canvasPoint = getCanvasCoordinates(event);
            drawingActions.startAreaErasing(canvasPoint);
            drawingActions.updateEraser(canvasPoint);
            
            // Set this as the primary drawing touch for area erasers too
            if (!primaryDrawingTouch) {
                palmActions.setPrimaryDrawingTouch(touchId);
            }
            return;
        }
        
        // Step 5: Create isolated stroke for this specific touch
        const newStroke = {
            id: `stroke_${Date.now()}_${Math.random()}`,
            touchId: touchId, // Associate stroke with specific touch
            tool: tool,
            style: { ...toolSettings },
            points: [{ ...strokePoint, timestamp: Date.now() }],
            created_at: new Date().toISOString()
        };
        
        // Set this as the primary drawing touch if no other touch is drawing
        if (!primaryDrawingTouch) {
            palmActions.setPrimaryDrawingTouch(touchId);
        }
        
        // Store the stroke for this touch ID
        palmActions.setActiveStrokesByTouch(prev => new Map(prev.set(touchId, newStroke)));
        
        // Initialize ref and start drawing
        currentStrokeRef.current = newStroke;
        drawingActions.startDrawing(newStroke);
        
        // Immediately draw a dot for single clicks (visual feedback)
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        
        ctx.save();
        ctx.strokeStyle = toolSettings.color;
        ctx.lineWidth = toolSettings.thickness;
        ctx.globalAlpha = toolSettings.opacity;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        // Draw a small dot at the click point
        ctx.beginPath();
        ctx.arc(strokePoint.x, strokePoint.y, toolSettings.thickness / 2, 0, 2 * Math.PI);
        ctx.fill();
        
        ctx.restore();
    };

    // Continue drawing
    const continueDrawing = (event) => {
        if (isRulerInteracting()) return;
        if (!isDrawing && !isAreaErasing) return;
        
        event.preventDefault();
        
        // Step 1: Get the specific touch for this event
        let touchToCheck = null;
        let touchId = 'pointer';
        
        if (event.touches && event.touches.length > 0) {
            touchToCheck = event.touches[0];
            touchId = touchToCheck.identifier;
        } else if (event.changedTouches && event.changedTouches.length > 0) {
            touchToCheck = event.changedTouches[0];
            touchId = touchToCheck.identifier;
        }
        
        // Step 2: IMMEDIATE rejection check - if this touch is already rejected, ignore completely
        if ((rejectedTouches instanceof Set) && (rejectedTouches instanceof Set) && rejectedTouches.has(touchId)) {
            return; // Don't process rejected touches at all
        }
        
        // Step 3: Quick palm rejection for new large touches during drawing
        if (palmRejectionSettings.enabled && touchToCheck) {
            const touchArea = calculateTouchArea(touchToCheck);
            const areaThreshold = getAreaThreshold(palmRejectionSettings.sensitivity);
            
            // Immediate rejection for large touch areas during drawing
            if (touchArea > areaThreshold) {
                palmActions.addRejectedTouch(touchId);
                return;
            }
        }
        
        // Step 4: Additional contextual rejection
        if (touchToCheck && shouldRejectTouch(touchToCheck, updateInputMethod(event))) {
            palmActions.addRejectedTouch(touchId);
            return;
        }
        
        // Update active touches
        updateActiveTouch(event, 'move');
        
        // Step 5: Touch isolation - only continue stroke for the correct touch
        // Only process movement for the primary drawing touch to prevent cross-contamination
        if (primaryDrawingTouch && touchId !== primaryDrawingTouch) {
            return;
        }
        
        // Get the current stroke for this specific touch (only for drawing tools, not erasers)
        const touchStroke = (activeStrokesByTouch instanceof Map) ? activeStrokesByTouch.get(touchId) : null;
        
        // For erasers, we don't need stroke tracking, just continue the eraser operation
        if (tool === 'eraser' || tool === 'square-eraser' || tool === 'circle-eraser') {
            // Eraser tools are handled separately below, no stroke validation needed
        } else if (!touchStroke && !currentStroke) {
            return;
        }
        
        // Step 6: Optimized multi-touch handling during drawing
        if (palmRejectionSettings.enabled && (activeTouches instanceof Map) && activeTouches.size > 1) {
            const primaryTouch = getPrimaryTouch(activeTouches);
        }

        let point = getCanvasCoordinates(event);
        let strokePoint = point;
        if (rulerVisible && isPointNearRuler(point, 30)) {
            strokePoint = projectPointOntoRuler(point);
        }
        
        // Apply coordinate transformation for infinite page mode
        if (pageMode === 'infinite') {
            strokePoint = transformCoordinates(strokePoint);
        }
        
        if (tool === 'eraser') {
            // Continue erasing
            handleErase(getCanvasCoordinates(event));
            return;
        }
        
        if (tool === 'square-eraser' || tool === 'circle-eraser') {
            // Update area eraser end point
            if (isAreaErasing) {
                drawingActions.updateEraser(getCanvasCoordinates(event));
                // Redraw canvas to show updated preview (throttled)
                redrawThrottlerRef.current?.requestRedraw() || redrawCanvas();
            }
            return;
        }
        
        // Use ref instead of state to avoid React re-renders during drawing
        const activeStroke = currentStrokeRef.current || currentStroke;
        if (!activeStroke) return;
        
        // Add some smoothing by checking distance from last point
        // IMPORTANT: Use strokePoint (transformed) for distance calculation since lastPoint is also transformed
        const distance = Math.sqrt(
            Math.pow(strokePoint.x - lastPoint.x, 2) + Math.pow(strokePoint.y - lastPoint.y, 2)
        );
        
        // Only add point if it's moved a minimum distance (reduces jitter)
        // Use smaller threshold for better capture of small details like dots
        if (distance > 1) { // Reduced from 2 to 1 for better small stroke capture
            const updatedStroke = {
                ...activeStroke,
                points: [...activeStroke.points, { ...strokePoint, timestamp: Date.now() }]
            };
            
            // Update ref directly during drawing (no React re-render)
            currentStrokeRef.current = updatedStroke;
            drawingActions.updateStroke(updatedStroke, strokePoint);
            
            // Draw the current stroke in real-time with smooth curves
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            
            ctx.save();
            ctx.strokeStyle = toolSettings.color;
            ctx.lineWidth = toolSettings.thickness;
            ctx.globalAlpha = toolSettings.opacity;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            
            const points = updatedStroke.points;
            if (points.length >= 3) {
                // Draw smooth curve for the last few points
                const len = points.length;
                const p1 = points[len - 3];
                const p2 = points[len - 2];
                const p3 = points[len - 1];
                
                // Calculate midpoint for smooth curve
                const midX = (p2.x + p3.x) / 2;
                const midY = (p2.y + p3.y) / 2;
                
                ctx.beginPath();
                ctx.moveTo(p1.x, p1.y);
                ctx.quadraticCurveTo(p2.x, p2.y, midX, midY);
                ctx.stroke();
            } else if (points.length === 2) {
                // For the first segment, draw a straight line
                ctx.beginPath();
                ctx.moveTo(points[0].x, points[0].y);
                ctx.lineTo(points[1].x, points[1].y);
                ctx.stroke();
            }
            
            ctx.restore();
        }
    };

    // Stop drawing
    const stopDrawing = (event) => {
        if (isRulerInteracting()) {
            drawingActions.stopDrawing();
            return;
        }
        
        if (!isDrawing && !isAreaErasing) return;
        
        event.preventDefault();
        
        // Check if this touch was rejected
        let touchToCheck = null;
        if (event.changedTouches && event.changedTouches.length > 0) {
            touchToCheck = event.changedTouches[0];
        }
        
        const touchId = touchToCheck?.identifier || 'pointer';
        
        // Update active touches and clean up rejected touches
        updateActiveTouch(event, 'end');

        // Clean up MultiFactorPalmDetector touch history
        if (palmDetector.current && touchId) {
            palmDetector.current.cleanupTouch(touchId);
        }

        // Remove this touch from rejected set
        if (rejectedTouches.has(touchId)) {
            palmActions.removeRejectedTouch(touchId);
            // If this was a rejected touch, don't process the drawing end
            return;
        }
        
        // Step 5: Touch isolation cleanup
        // Only process end events for the primary drawing touch
        if (primaryDrawingTouch && touchId !== primaryDrawingTouch) {
            // Clean up this touch from tracking
            palmActions.setActiveStrokesByTouch(prev => {
                const newMap = new Map(prev);
                newMap.delete(touchId);
                return newMap;
            });
            return;
        }
        
        // Clean up the primary drawing touch
        if (touchId === primaryDrawingTouch) {
            palmActions.setPrimaryDrawingTouch(null);
            palmActions.setActiveStrokesByTouch(prev => {
                const newMap = new Map(prev);
                newMap.delete(touchId);
                return newMap;
            });
        }
        
        // ===== RULER INTERACTION LOGIC =====
        if (rulerVisible) {
            if (isDraggingRuler) {
                rulerActions.stopDragging();
                redrawThrottlerRef.current?.requestRedraw() || redrawCanvas();
                return;
            }
            if (isRotatingRuler) {
                rulerActions.stopRotating();
                redrawThrottlerRef.current?.requestRedraw() || redrawCanvas();
                return;
            }
        }
        // ===== END RULER INTERACTION LOGIC =======

        if (tool === 'eraser') {
            // Point eraser doesn't need to save a stroke
            drawingActions.stopDrawing();
            
            // Clean up touch isolation for point eraser
            if (touchId === primaryDrawingTouch) {
                palmActions.setPrimaryDrawingTouch(null);
            }
            return;
        }
        
        if (tool === 'square-eraser' || tool === 'circle-eraser') {
            // Complete area erasing
            if (isAreaErasing && eraserStartPoint && eraserEndPoint) {
                // Check if the eraser area is large enough to be meaningful
                const distanceX = Math.abs(eraserEndPoint.x - eraserStartPoint.x);
                const distanceY = Math.abs(eraserEndPoint.y - eraserStartPoint.y);
                const minSize = 10; // Minimum size in pixels
                
                if (distanceX > minSize || distanceY > minSize) {
                    const shape = tool === 'square-eraser' ? 'square' : 'circle';
                    // Transform eraser coordinates to drawing space for proper stroke comparison
                    const transformedStart = transformCoordinates(eraserStartPoint);
                    const transformedEnd = transformCoordinates(eraserEndPoint);
                    handleAreaErase(strokes, transformedStart, transformedEnd, shape, onStrokeAdded);
                }
            }
            
            // Reset area erasing state
            drawingActions.stopAreaErasing();
            
            // Clean up touch isolation for area eraser
            if (touchId === primaryDrawingTouch) {
                palmActions.setPrimaryDrawingTouch(null);
            }
            
            // Redraw canvas to remove preview (throttled)
            redrawThrottlerRef.current?.requestRedraw() || redrawCanvas();
            return;
        }
        
        // Get the final stroke from ref BEFORE stopping drawing
        // (which clears the ref in the action)
        const finalStroke = currentStrokeRef.current || currentStroke;
        
        drawingActions.stopDrawing();
        
        // Add the completed stroke
        if (finalStroke && finalStroke.points.length > 0) {
            // Handle single-click dots by duplicating the point to make a small stroke
            if (finalStroke.points.length === 1) {
                const point = finalStroke.points[0];
                // Create a very small stroke for dots by adding a second point slightly offset
                const dotStroke = {
                    ...finalStroke,
                    points: [
                        point,
                        { 
                            x: point.x + 0.1, 
                            y: point.y + 0.1, 
                            pressure: point.pressure, 
                            timestamp: point.timestamp + 1 
                        }
                    ]
                };
                
                if (onStrokeAdded) {
                    onStrokeAdded(dotStroke);
                }
            } else {
                // Normal stroke with multiple points
                if (onStrokeAdded) {
                    onStrokeAdded(finalStroke);
                }
            }
        }
        
        // Ref is already cleared by stopDrawing action
    };

    // Handle eraser functionality
    const handleErase = (point) => {
        const eraserRadius = toolSettings.thickness * 2; // Eraser is bigger than brush
        const strokesToRemove = [];
        
        // Transform the point to drawing coordinates for stroke comparison
        const drawingPoint = transformCoordinates(point);
        
        strokes.forEach((stroke, index) => {
            // Check if any point in the stroke is within the eraser radius
            const shouldErase = stroke.points.some(strokePoint => {
                const distance = Math.sqrt(
                    Math.pow(drawingPoint.x - strokePoint.x, 2) + 
                    Math.pow(drawingPoint.y - strokePoint.y, 2)
                );
                return distance <= eraserRadius;
            });
            
            if (shouldErase) {
                strokesToRemove.push(stroke);
            }
        });
        
        // Remove the strokes that were erased
        if (strokesToRemove.length > 0 && onStrokeAdded) {
            strokesToRemove.forEach(strokeToRemove => {
                // Send a special "erase" event to the parent component
                onStrokeAdded({
                    action: 'erase',
                    strokeId: strokeToRemove.id,
                    stroke: strokeToRemove
                });
            });
        }
    };

    // --- Modified pointer event handlers ---
    const cancelDrawingState = () => {
        drawingActions.stopDrawing();
    };

    // Text tool handling
    const handleTextToolClick = (point) => {
        // Position the text input at the clicked location
        textActions.show(point);
        textActions.updateValue('');
    };

    const handleTextInputSubmit = () => {
        if (textInputValue.trim() && textInputPosition) {
            // Create a text stroke - we'll render text as a special stroke type
            const textStroke = {
                id: `text_${Date.now()}_${Math.random()}`,
                type: 'text',
                text: textInputValue,
                points: [textInputPosition], // Single point for text position
                style: {
                    color: toolSettings.color,
                    thickness: toolSettings.thickness,
                    opacity: 1
                },
                tool: 'text',
                timestamp: Date.now(),
                created_at: new Date().toISOString(),
                isTemporary: false
            };

            // Add stroke using the callback
            if (onStrokeAdded) {
                onStrokeAdded(textStroke);
            }
        }
        
        // Hide text input
        textActions.hide();
        textActions.updateValue('');
    };

    const handleTextInputCancel = () => {
        textActions.hide();
        textActions.updateValue('');
    };

    const handlePointerDown = (event) => {
        const point = getCanvasCoordinates(event);
        
        // Handle text tool - create text input at click position
        if (tool === 'text') {
            handleTextToolClick(point);
            return;
        }
        
        // Handle selection tool interactions
        if (tool === 'select') {
            // Check if clicking on rotation handle
            if (selectionBounds && getRotationHandleAtPoint(point, selectionBounds)) {
                // Store original points and rotation before starting rotation
                const originalPoints = new Map();
                const originalData = new Map();
                strokes.forEach(stroke => {
                    if (selectedStrokes.has(stroke.id)) {
                        originalPoints.set(stroke.id, [...stroke.points]);
                        originalData.set(stroke.id, {
                            rotation: stroke.rotation || 0
                        });
                    }
                });
                selectionActions.setOriginalPoints(originalPoints);
                selectionActions.setOriginalData(originalData);
                
                // Calculate initial angle
                const centerX = selectionBounds.x + selectionBounds.width / 2;
                const centerY = selectionBounds.y + selectionBounds.height / 2;
                const initialAngle = Math.atan2(point.y - centerY, point.x - centerX);
                
                selectionActions.startRotating(initialAngle);
                selectionActions.updateRotation(0);
                selectionActions.startDragging(point);
                return;
            }
            
            // Check if clicking on resize handle
            if (selectionBounds) {
                const handle = getResizeHandleAtPoint(point, selectionBounds);
                if (handle) {
                    // Store original points before starting resize
                    const originalPoints = new Map();
                    strokes.forEach(stroke => {
                        if (selectedStrokes.has(stroke.id)) {
                            originalPoints.set(stroke.id, [...stroke.points]);
                        }
                    });
                    selectionActions.setOriginalPoints(originalPoints);
                    
                    selectionActions.startResizing(handle);
                    selectionActions.startDragging(point);
                    return;
                }
                
                // Check if clicking inside selection bounds to start dragging
                if (isPointInSelectionBounds(point, selectionBounds)) {
                    // Store original points before starting drag
                    const originalPoints = new Map();
                    strokes.forEach(stroke => {
                        if (selectedStrokes.has(stroke.id)) {
                            originalPoints.set(stroke.id, [...stroke.points]);
                        }
                    });
                    selectionActions.setOriginalPoints(originalPoints);
                    
                    selectionActions.startDragging(point);
                    return;
                }
            }
            
            // Otherwise start new selection
            selectionActions.selectStrokes([]);
            selectionActions.startSelecting(point);
            selectionActions.setSelectionBox({ x: point.x, y: point.y, width: 0, height: 0 });
            return;
        }
        
        if (rulerVisible) {
            if (isPointNearRulerEndpoint(point, rulerStart, 20)) {
                cancelDrawingState();
                rulerActions.startRotating('start');
                rulerActions.startDragging({ x: point.x - rulerStart.x, y: point.y - rulerStart.y });
                return; // Do NOT start drawing
            }
            if (isPointNearRulerEndpoint(point, rulerEnd, 20)) {
                cancelDrawingState();
                rulerActions.startRotating('end');
                rulerActions.startDragging({ x: point.x - rulerEnd.x, y: point.y - rulerEnd.y });
                return; // Do NOT start drawing
            }
            if (isPointOnRulerBody(point, 15)) {
                cancelDrawingState();
                rulerActions.startDragging({ x: point.x - rulerStart.x, y: point.y - rulerStart.y });
                return; // Do NOT start drawing
            }
        }
        // Only start drawing if not interacting with ruler
        startDrawing(event);
    };

    const handlePointerMove = (event) => {
        const point = getCanvasCoordinates(event);
        
        // Handle selection tool interactions
        if (tool === 'select') {
            // Update cursor based on what's under the mouse
            updateSelectionCursor(point);
            
            if (isSelecting && selectionStartPoint) {
                // Update selection rectangle
                const x = Math.min(point.x, selectionStartPoint.x);
                const y = Math.min(point.y, selectionStartPoint.y);
                const width = Math.abs(point.x - selectionStartPoint.x);
                const height = Math.abs(point.y - selectionStartPoint.y);
                selectionActions.setSelectionBox({ x, y, width, height });
                redrawThrottlerRef.current?.requestRedraw() || redrawCanvas();
                return;
            }
            
            if (isDraggingSelection && dragStartPoint && selectionBounds) {
                // Move selected strokes - update local state only, don't call API yet
                const offsetX = point.x - dragStartPoint.x;
                const offsetY = point.y - dragStartPoint.y;
                
                // Transform strokes using original points for accurate calculations
                const updatedStrokes = [];
                strokes.forEach(stroke => {
                    if (selectedStrokes.has(stroke.id)) {
                        const originalPoints = originalStrokePoints.get(stroke.id) || stroke.points;
                        const transformedPoints = transformStrokePoints(originalPoints, { offsetX, offsetY });
                        updatedStrokes.push({
                            ...stroke,
                            points: transformedPoints
                        });
                    }
                });
                
                // Store transformed strokes locally for visual feedback
                selectionActions.setTransformedStrokes(updatedStrokes);
                redrawThrottlerRef.current?.requestRedraw() || redrawCanvas();
                return;
            }
            
            if (isRotatingSelection && dragStartPoint && selectionBounds) {
                // Rotate selected strokes - update local state only, don't call API yet
                const centerX = selectionBounds.x + selectionBounds.width / 2;
                const centerY = selectionBounds.y + selectionBounds.height / 2;
                
                // Calculate current angle
                const currentAngle = Math.atan2(point.y - centerY, point.x - centerX);
                
                // Calculate rotation delta from initial angle
                const rotationDelta = currentAngle - initialRotationAngle;
                selectionActions.updateRotation(rotationDelta);
                
                // Transform strokes using original points for accurate calculations
                const updatedStrokes = [];
                strokes.forEach(stroke => {
                    if (selectedStrokes.has(stroke.id)) {
                        const originalPoints = originalStrokePoints.get(stroke.id) || stroke.points;
                        const originalData = originalStrokeData.get(stroke.id) || { rotation: 0 };
                        
                        if (stroke.type === 'text') {
                            // For text strokes, rotate the position and store the cumulative rotation angle
                            const transformedPoints = transformStrokePoints(originalPoints, { 
                                rotation: rotationDelta, 
                                originX: centerX, 
                                originY: centerY 
                            });
                            
                            // Calculate the new rotation angle (original + delta)
                            const newRotation = originalData.rotation + rotationDelta;
                            
                            updatedStrokes.push({
                                ...stroke,
                                points: transformedPoints,
                                rotation: newRotation // Store cumulative rotation for text rendering
                            });
                        } else {
                            // For regular strokes, just rotate the points
                            const transformedPoints = transformStrokePoints(originalPoints, { 
                                rotation: rotationDelta, 
                                originX: centerX, 
                                originY: centerY 
                            });
                            updatedStrokes.push({
                                ...stroke,
                                points: transformedPoints
                            });
                        }
                    }
                });
                
                // Store transformed strokes locally for visual feedback
                selectionActions.setTransformedStrokes(updatedStrokes);
                redrawThrottlerRef.current?.requestRedraw() || redrawCanvas();
                return;
            }
            
            if (isResizingSelection && dragStartPoint && selectionBounds && resizeHandle) {
                // Resize selected strokes - update local state only, don't call API yet
                const deltaX = point.x - dragStartPoint.x;
                const deltaY = point.y - dragStartPoint.y;
                
                let scaleX = 1, scaleY = 1;
                let originX = selectionBounds.x;
                let originY = selectionBounds.y;
                
                // Calculate scale based on resize handle
                switch (resizeHandle) {
                    case 'se': // Southeast corner
                        scaleX = (selectionBounds.width + deltaX) / selectionBounds.width;
                        scaleY = (selectionBounds.height + deltaY) / selectionBounds.height;
                        break;
                    case 'nw': // Northwest corner
                        scaleX = (selectionBounds.width - deltaX) / selectionBounds.width;
                        scaleY = (selectionBounds.height - deltaY) / selectionBounds.height;
                        originX = selectionBounds.x + selectionBounds.width;
                        originY = selectionBounds.y + selectionBounds.height;
                        break;
                    case 'ne': // Northeast corner
                        scaleX = (selectionBounds.width + deltaX) / selectionBounds.width;
                        scaleY = (selectionBounds.height - deltaY) / selectionBounds.height;
                        originY = selectionBounds.y + selectionBounds.height;
                        break;
                    case 'sw': // Southwest corner
                        scaleX = (selectionBounds.width - deltaX) / selectionBounds.width;
                        scaleY = (selectionBounds.height + deltaY) / selectionBounds.height;
                        originX = selectionBounds.x + selectionBounds.width;
                        break;
                    case 'e': // East side
                        scaleX = (selectionBounds.width + deltaX) / selectionBounds.width;
                        originY = selectionBounds.y + selectionBounds.height / 2;
                        break;
                    case 'w': // West side
                        scaleX = (selectionBounds.width - deltaX) / selectionBounds.width;
                        originX = selectionBounds.x + selectionBounds.width;
                        originY = selectionBounds.y + selectionBounds.height / 2;
                        break;
                    case 's': // South side
                        scaleY = (selectionBounds.height + deltaY) / selectionBounds.height;
                        originX = selectionBounds.x + selectionBounds.width / 2;
                        break;
                    case 'n': // North side
                        scaleY = (selectionBounds.height - deltaY) / selectionBounds.height;
                        originX = selectionBounds.x + selectionBounds.width / 2;
                        originY = selectionBounds.y + selectionBounds.height;
                        break;
                }
                
                // Prevent negative scaling
                scaleX = Math.max(0.1, scaleX);
                scaleY = Math.max(0.1, scaleY);
                
                // Transform strokes using original points for accurate calculations
                const updatedStrokes = [];
                strokes.forEach(stroke => {
                    if (selectedStrokes.has(stroke.id)) {
                        const originalPoints = originalStrokePoints.get(stroke.id) || stroke.points;
                        const transformedPoints = transformStrokePoints(originalPoints, { scaleX, scaleY, originX, originY });
                        updatedStrokes.push({
                            ...stroke,
                            points: transformedPoints
                        });
                    }
                });
                
                // Store transformed strokes locally for visual feedback
                selectionActions.setTransformedStrokes(updatedStrokes);
                redrawThrottlerRef.current?.requestRedraw() || redrawCanvas();
                return;
            }
            
            return; // Don't fall through to drawing logic
        }
        
        if (rulerVisible) {
            if (isDraggingRuler) {
                // Move both endpoints by drag offset
                const dx = point.x - rulerStart.x - dragOffset.x;
                const dy = point.y - rulerStart.y - dragOffset.y;
                const newStart = { x: rulerStart.x + dx, y: rulerStart.y + dy };
                const newEnd = { x: rulerEnd.x + dx, y: rulerEnd.y + dy };
                rulerActions.updatePosition(newStart, newEnd);
                redrawThrottlerRef.current?.requestRedraw() || redrawCanvas();
                return;
            }
            if (isRotatingRuler && rotatingEndpoint) {
                // Move only the selected endpoint
                if (rotatingEndpoint === 'start') {
                    const newStart = { x: point.x - dragOffset.x, y: point.y - dragOffset.y };
                    rulerActions.updatePosition(newStart, rulerEnd);
                } else {
                    const newEnd = { x: point.x - dragOffset.x, y: point.y - dragOffset.y };
                    rulerActions.updatePosition(rulerStart, newEnd);
                }
                redrawThrottlerRef.current?.requestRedraw() || redrawCanvas();
                return;
            }
        }
        // Only continue drawing if not interacting with ruler
        continueDrawing(event);
    };

    const handlePointerUp = (event) => {
        // Handle text tool - don't process stroke events
        if (tool === 'text') {
            return;
        }
        
        // Handle selection tool interactions
        if (tool === 'select') {
            if (isSelecting && selectionBox) {
                // Complete selection - find strokes in selection box
                const selectedIds = getStrokesInSelection(selectionBox, strokes);
                selectionActions.selectStrokes(Array.isArray(selectedIds) ? selectedIds : Array.from(selectedIds));
                selectionActions.stopSelecting();
                selectionActions.setSelectionBox(null);
                redrawThrottlerRef.current?.requestRedraw() || redrawCanvas();
                return;
            }
            
            if (isDraggingSelection) {
                // Finalize move operation - trigger API call once with transformed strokes
                if (transformedStrokes.length > 0 && onStrokesUpdated) {
                    onStrokesUpdated(transformedStrokes);
                }
                
                // Clear states
                selectionActions.setTransformedStrokes([]);
                selectionActions.setOriginalPoints(new Map());
                selectionActions.setOriginalData(new Map());
                selectionActions.stopDragging();
                return;
            }
            
            if (isRotatingSelection) {
                // Finalize rotation operation - trigger API call once with transformed strokes
                if (transformedStrokes.length > 0 && onStrokesUpdated) {
                    onStrokesUpdated(transformedStrokes);
                }
                
                // Clear states
                selectionActions.setTransformedStrokes([]);
                selectionActions.setOriginalPoints(new Map());
                selectionActions.setOriginalData(new Map());
                selectionActions.stopRotating();
                selectionActions.updateRotation(0);
                return;
            }
            
            if (isResizingSelection) {
                // Finalize resize operation - trigger API call once with transformed strokes
                if (transformedStrokes.length > 0 && onStrokesUpdated) {
                    onStrokesUpdated(transformedStrokes);
                }
                
                // Clear states
                selectionActions.setTransformedStrokes([]);
                selectionActions.setOriginalPoints(new Map());
                selectionActions.setOriginalData(new Map());
                selectionActions.stopResizing();
                return;
            }
            
            return; // Don't fall through to drawing logic
        }
        
        if (rulerVisible) {
            if (isDraggingRuler) {
                rulerActions.stopDragging();
                redrawThrottlerRef.current?.requestRedraw() || redrawCanvas();
                return;
            }
            if (isRotatingRuler) {
                rulerActions.stopRotating();
                redrawThrottlerRef.current?.requestRedraw() || redrawCanvas();
                return;
            }
        }
        // Only stop drawing if not interacting with ruler
        stopDrawing(event);
    };

    // Mouse events (fallback)
    const handleMouseDown = (event) => {
        if (tool === 'select' || isRulerInteracting()) return;
        startDrawing(event);
    };
    const handleMouseMove = (event) => {
        if (tool === 'select' || isRulerInteracting()) return;
        continueDrawing(event);
    };
    const handleMouseUp = (event) => {
        if (tool === 'select') return;
        if (isRulerInteracting()) {
            drawingActions.stopDrawing();
            return;
        }
        stopDrawing(event);
    };
    const handleTouchStart = (event) => {
        // Handle scrolling for infinite pages - if it returns true, don't continue with drawing
        if (handleTouchScroll(event)) return;
        
        if (tool === 'select' || isRulerInteracting()) return;
        
        // Don't start drawing if it's a two-finger gesture
        if (event.touches && event.touches.length > 1) return;
        
        startDrawing(event);
    };
    const handleTouchMove = (event) => {
        // Handle scrolling for infinite pages - if it returns true, don't continue with drawing
        if (handleTouchScroll(event)) return;
        
        if (tool === 'select' || isRulerInteracting()) return;
        
        // Don't continue drawing if it's a two-finger gesture or pinch zooming
        if (event.touches && (event.touches.length > 1 || isPinchZooming)) return;
        
        continueDrawing(event);
    };
    const handleTouchEnd = (event) => {
        // Handle scrolling end - if it returns true, don't continue with drawing
        if (handleTouchScroll(event)) return;
        
        // Handle scrolling cleanup
        if (event.touches && event.touches.length === 0) {
            scrollActions.stopPinchZoom();
            scrollActions.setLastTouchDistance(0);
        }
        
        if (tool === 'select') return;
        if (isRulerInteracting()) {
            drawingActions.stopDrawing();
            return;
        }
        stopDrawing(event);
    };

    return (
        <div 
            ref={containerRef}
            className={`drawing-canvas-container ${
                palmRejectionSettings.enabled ? 
                    (rejectedTouches.size > 0 ? 'palm-rejection-blocking' : 'palm-rejection-active') 
                    : ''
            } ${isCanvasLargerThanContainer ? 'canvas-larger-than-container' : ''}`} 
            data-page-mode={pageMode || 'scaling'}
        >
            <canvas
                ref={canvasRef}
                className="drawing-canvas"
                // Pointer events (primary - best for stylus detection)
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerUp}
                onPointerCancel={handlePointerUp}
                // Mouse events (fallback for older browsers)
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                // Touch events (additional fallback)
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchEnd}
                // Scroll events for infinite pages
                onWheel={handleWheel}
                onContextMenu={handleContextMenu} // Handle context menu for stroke saving
                onDragStart={(e) => e.preventDefault()} // Prevent drag
                style={{
                    width: `${width}px`,
                    height: `${height}px`,
                    cursor: tool === 'pen' ? 'crosshair' : 
                           tool === 'highlighter' ? 'crosshair' :
                           tool === 'eraser' ? 'grab' : 
                           tool === 'square-eraser' ? 'crosshair' :
                           tool === 'circle-eraser' ? 'crosshair' :
                           tool === 'select' ? selectionCursor : 'default',
                    touchAction: 'none' // We handle all touch interactions manually
                }}
            />
            
            {/* Scroll Position Indicator for Infinite Mode */}
            {pageMode === 'infinite' && (scrollOffset.x !== 0 || scrollOffset.y !== 0) && (
                <div style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    background: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    fontWeight: '500',
                    pointerEvents: 'none',
                    zIndex: 10,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                }}>
                    <span style={{ fontSize: '10px' }}>📐</span>
                    <span>
                        {scrollOffset.x !== 0 && `X: ${Math.round(scrollOffset.x)}px`}
                        {scrollOffset.x !== 0 && scrollOffset.y !== 0 && ' | '}
                        {scrollOffset.y !== 0 && `Y: ${Math.round(scrollOffset.y)}px`}
                    </span>
                </div>
            )}
            
            {/* Palm Rejection Status Indicator */}
            {palmRejectionSettings.enabled && (
                <div className="palm-rejection-status" style={{
                    position: pageMode === 'infinite' ? 'fixed' : 'absolute',
                    top: '10px',
                    right: '10px',
                    background: rejectedTouches.size > 0 ? 'rgba(255, 99, 99, 0.9)' : 'rgba(76, 175, 80, 0.9)',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    fontWeight: '500',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    zIndex: 10,
                    pointerEvents: 'none',
                    transition: 'all 0.2s ease',
                    opacity: ((activeTouches instanceof Map) && activeTouches.size > 0) || ((rejectedTouches instanceof Set) && rejectedTouches.size > 0) ? 1 : 0.7
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ 
                            display: 'inline-block',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: rejectedTouches.size > 0 ? '#ff4444' : '#4CAF50',
                            boxShadow: rejectedTouches.size > 0 ? '0 0 6px rgba(255, 68, 68, 0.6)' : '0 0 6px rgba(76, 175, 80, 0.6)'
                        }} />
                        <span>
                            {rejectedTouches.size > 0 ? '🛡️ Palm Blocked' : '🛡️ Palm Protection'}
                        </span>
                    </div>
                    <div style={{ 
                        fontSize: '10px', 
                        marginTop: '2px', 
                        opacity: 0.9,
                        fontWeight: '400'
                    }}>
                        {currentInputMethod === 'stylus' && '✏️ Stylus'}
                        {currentInputMethod === 'finger' && '👆 Finger'}
                        {currentInputMethod === 'mouse' && '🖱️ Mouse'}
                        {currentInputMethod === 'unknown' && '❓ Unknown'}
                        {(activeTouches instanceof Map) && activeTouches.size > 1 && ` • ${activeTouches.size} touches`}
                        {(rejectedTouches instanceof Set) && rejectedTouches.size > 0 && ` • ${rejectedTouches.size} rejected`}
                    </div>
                </div>
            )}
            
            {/* Touch Visualization (Debug Mode) */}
            {palmRejectionSettings.enabled && palmRejectionSettings.showDebug && (
                <div className="touch-debug-overlay" style={{
                    position: pageMode === 'infinite' ? 'fixed' : 'absolute',
                    top: '10px',
                    left: '10px',
                    background: 'rgba(0, 0, 0, 0.9)',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    fontSize: '11px',
                    fontFamily: 'monospace',
                    maxWidth: '380px',
                    zIndex: 10,
                    pointerEvents: 'none',
                    lineHeight: '1.4'
                }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#60a5fa' }}>
                        🎯 MultiFactorPalmDetector Debug
                    </div>
                    
                    <div style={{ marginBottom: '6px' }}>
                        <span style={{ color: '#fbbf24' }}>Settings:</span>
                        <div style={{ marginLeft: '8px', fontSize: '10px' }}>
                            Sensitivity: {palmRejectionSettings.sensitivity}
                            <br />Input Mode: {palmRejectionSettings.inputMode}
                            <br />Device: {palmDetector.current?.settings?.deviceType || 'unknown'}
                            <br />Area Threshold: {getAreaThreshold(palmRejectionSettings.sensitivity).toFixed(0)}px²
                        </div>
                    </div>
                    
                    <div style={{ marginBottom: '6px' }}>
                        <span style={{ color: '#34d399' }}>Touch Status:</span>
                        <div style={{ marginLeft: '8px', fontSize: '10px' }}>
                            Active: {(activeTouches instanceof Map) ? activeTouches.size : 0} | Rejected: {(rejectedTouches instanceof Set) ? rejectedTouches.size : 0}
                            <br />Input: {currentInputMethod}
                            <br />Primary Drawing: {primaryDrawingTouch || 'none'}
                            <br />Active Strokes: {(activeStrokesByTouch instanceof Map) ? activeStrokesByTouch.size : 0}
                            {(activeTouches instanceof Map) && activeTouches.size > 1 && (() => {
                                const primary = getPrimaryTouch(activeTouches);
                                return primary ? <><br />Primary Touch: {primary.inputMethod} ({primary.area.toFixed(0)}px²)</> : null;
                            })()}
                        </div>
                    </div>
                    
                    {palmDetector.current && (
                        <div style={{ marginBottom: '6px' }}>
                            <span style={{ color: '#a78bfa' }}>Multi-Factor Analysis:</span>
                            <div style={{ marginLeft: '8px', fontSize: '10px' }}>
                                Factor Weights: Area({(palmDetector.current.settings.weights.contactArea * 100).toFixed(0)}%) | 
                                Pressure({(palmDetector.current.settings.weights.pressure * 100).toFixed(0)}%) | 
                                Velocity({(palmDetector.current.settings.weights.velocity * 100).toFixed(0)}%)
                                <br />Multi-touch({(palmDetector.current.settings.weights.multiTouch * 100).toFixed(0)}%) | 
                                Device({(palmDetector.current.settings.weights.temporal * 100).toFixed(0)}%)
                            </div>
                        </div>
                    )}
                    
                    {(recentRejections || []).length > 0 && (
                        <div style={{ marginBottom: '6px' }}>
                            <span style={{ color: '#f87171' }}>Recent Rejections:</span>
                            {(recentRejections || []).slice(-3).map((rejection, index) => {
                                const age = Date.now() - rejection.timestamp;
                                return (
                                    <div key={index} style={{ 
                                        fontSize: '9px', 
                                        marginTop: '2px',
                                        marginLeft: '8px',
                                        color: '#ff9999',
                                        padding: '1px 4px',
                                        background: 'rgba(255,100,100,0.1)',
                                        borderRadius: '3px'
                                    }}>
                                        🚫 {(rejection.confidence * 100).toFixed(1)}% {'>'} {(rejection.threshold * 100).toFixed(0)}%
                                        <br />{rejection.reasoning} ({age}ms ago)
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    
                    {(activeTouches?.size || 0) > 0 && (
                        <div style={{ marginBottom: '6px' }}>
                            <span style={{ color: '#60a5fa' }}>Active Touches:</span>
                            {Array.from((activeTouches || new Map()).values()).map((touch, index) => {
                                const touchAge = Date.now() - touch.startTime;
                                const movement = Math.sqrt(
                                    Math.pow(touch.currentX - touch.startX, 2) +
                                    Math.pow(touch.currentY - touch.startY, 2)
                                );
                                
                                return (
                                    <div key={touch.id} style={{ 
                                        fontSize: '9px', 
                                        marginTop: '2px',
                                        marginLeft: '8px',
                                        color: (rejectedTouches instanceof Set) && rejectedTouches.has(touch.id) ? '#ff6b6b' : '#60a5fa',
                                        padding: '1px 4px',
                                        background: 'rgba(255,255,255,0.1)',
                                        borderRadius: '3px'
                                    }}>
                                        🤚 Touch {touch.id}
                                        <br />Area: {touch.area.toFixed(0)}px² | P: {(touch.pressure * 100).toFixed(0)}%
                                        <br />Age: {touchAge}ms | Move: {movement.toFixed(0)}px
                                        <br />{touch.inputMethod}
                                        {(rejectedTouches instanceof Set) && rejectedTouches.has(touch.id) && 
                                            <span style={{ color: '#ff6b6b' }}> [REJECTED]</span>
                                        }
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    
                    {(recentRejections || []).length > 0 && (
                        <div style={{ fontSize: '9px', color: '#fbbf24' }}>
                            Recent rejections: {(recentRejections || []).length}
                            <br />Last: {(recentRejections || [])[((recentRejections || []).length - 1)]?.reason || 'unknown'}
                        </div>
                    )}
                </div>
            )}
            
            {/* Simplified Rejection Markers */}
            {palmRejectionSettings?.enabled && Array.isArray(recentRejections) && recentRejections.map(rejection => {
                const age = Date.now() - rejection.timestamp;
                const opacity = Math.max(0, 1 - (age / 2000)); // Fade out over 2 seconds
                const scale = Math.max(0.5, 1 - (age / 2000)); // Shrink over time
                
                // Optimized rejection reasons (Step 5)
                let markerColor = 'rgba(255, 68, 68, 0.8)'; // Default red
                let markerIcon = '❌';
                let markerTitle = 'Touch Rejected';
                
                if (rejection.reason === 'optimized_palm_rejection_final') {
                    markerColor = 'rgba(255, 68, 68, 0.8)';
                    markerIcon = '�️';
                    markerTitle = 'Optimized Palm Rejection';
                } else if (rejection.reason === 'secondary_touch') {
                    markerColor = 'rgba(255, 165, 0, 0.8)';
                    markerIcon = '✋';
                    markerTitle = 'Secondary Touch';
                } else if (rejection.reason === 'area_too_large') {
                    markerColor = 'rgba(138, 43, 226, 0.8)';
                    markerIcon = '📏';
                    markerTitle = 'Area Too Large';
                }
                
                return (
                    <div
                        key={rejection.id}
                        title={markerTitle}
                        style={{
                            position: 'absolute',
                            left: `${rejection.x - 15}px`,
                            top: `${rejection.y - 15}px`,
                            width: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            background: markerColor,
                            border: '2px solid rgba(255, 255, 255, 0.9)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            zIndex: 5,
                            opacity: opacity,
                            transform: `scale(${scale})`,
                            transition: 'opacity 0.1s ease, transform 0.1s ease',
                            pointerEvents: 'none',
                            boxShadow: `0 2px 8px rgba(0,0,0,0.3)`
                        }}
                    >
                        {markerIcon}
                        {palmRejectionSettings.showDebug && (
                            <div className='DrawingCanvas__show-debug'>
                                {rejection.reason}
                                {rejection.primaryTouchId && (
                                    <>
                                        <br />Primary: {rejection.primaryTouchId}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
            
            {/* Text input overlay */}
            {isTextInputVisible && textInputPosition && (
                <div
                    style={{
                        position: 'absolute',
                        left: `${textInputPosition.x}px`,
                        top: `${textInputPosition.y}px`,
                        zIndex: 10,
                        background: 'white',
                        border: '2px solid #007bff',
                        borderRadius: '4px',
                        padding: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                    }}
                >
                    <input
                        type="text"
                        value={textInputValue}
                        onChange={(e) => textActions.updateValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleTextInputSubmit();
                            } else if (e.key === 'Escape') {
                                handleTextInputCancel();
                            }
                        }}
                        onBlur={handleTextInputCancel}
                        autoFocus
                        style={{
                            border: 'none',
                            outline: 'none',
                            minWidth: '100px',
                            background: 'transparent',
                            fontSize: `${toolSettings.thickness * 8}px`,
                            color: toolSettings.color
                        }}
                        placeholder="Type text..."
                    />
                </div>
            )}
        </div>
    );
}

export default DrawingCanvas;
